import { useEffect, useMemo, useState } from "react";
import type { AdminCountriesListResponse, AdminCountryDto } from "@/lib/api/types";
import { apiJson } from "@/lib/api/client";
import {
  ADMIN_REGION_CHANGED_EVENT,
  buildAdminRegionOptions,
  readAdminRegionCookie,
  writeAdminRegionCookie,
} from "@/lib/admin-region";

type Props = {
  className?: string;
  hidden?: boolean;
};

export function AdminRegionSelect({ className, hidden }: Props) {
  const [region, setRegion] = useState(readAdminRegionCookie);
  const [countries, setCountries] = useState<AdminCountryDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const json = await apiJson<AdminCountriesListResponse>("/admin/countries");
        if (!cancelled) setCountries(json.items ?? []);
      } catch {
        if (!cancelled) setCountries([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onChange = (event: Event) => {
      const custom = event as CustomEvent<string>;
      const next = String(custom.detail ?? "").trim();
      if (next) setRegion(next);
    };
    window.addEventListener(ADMIN_REGION_CHANGED_EVENT, onChange as EventListener);
    return () => window.removeEventListener(ADMIN_REGION_CHANGED_EVENT, onChange as EventListener);
  }, []);

  const options = useMemo(
    () => buildAdminRegionOptions(countries, region),
    [countries, region]
  );

  if (hidden) return null;

  return (
    <label className="inline-flex min-w-0 items-center gap-2">
      <span className="hidden text-xs font-medium uppercase tracking-wide text-muted sm:inline">
        Country
      </span>
      <select
        id="SystemRegion"
        aria-label="Country"
        className={
          className ??
          "h-9 min-w-[9.5rem] rounded-lg border border-border bg-surface px-2 text-sm text-foreground"
        }
        value={
          options.find((o) => o.value.toUpperCase() === region.toUpperCase())?.value ?? region
        }
        onChange={(e) => {
          writeAdminRegionCookie(e.target.value);
          window.location.reload();
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
